import { NumberFormat } from '@instinet-analytics/core-lib';
import omitUndefined from 'utils/omitUndefined';
import {
    getBucketIncludingTime,
    IntraDayProfileDataItem,
} from '../chart/IntraDayProfileData';

export interface LegendData {
    auctionVolume?: string;
    actualVolume?: string;
    cumulativeActualVolume?: string;
    expectedVolume?: string;
    cumulativeExpectedVolume?: string;
    spread?: string;
    volatility?: string;
}

const defaultValue: LegendData = {};

export function deriveLegendData(
    time: number | undefined,
    dataSeries: IntraDayProfileDataItem[],
    numberFormat: NumberFormat
): LegendData {
    const getDefaultBucket: () => IntraDayProfileDataItem | undefined = () => {
        // If no time or time is out of range, we use the bucket incliding now, or the last bucket if there isn't a bucket for now...
        if (dataSeries.length === 0) {
            return undefined;
        }
        return (
            getBucketIncludingTime(dataSeries, new Date().getTime()) ??
            dataSeries[dataSeries.length - 1]
        );
    };

    if (time === undefined && dataSeries.length > 0) {
        time = getDefaultBucket()?.time;
    }

    if (!time) {
        return defaultValue;
    }

    const dataPoint =
        getBucketIncludingTime(dataSeries, time) ?? getDefaultBucket();

    if (!dataPoint) {
        return defaultValue;
    }

    return omitUndefined<LegendData>({
        auctionVolume: getLegendValue(dataPoint.auctionVolume, numberFormat),
        actualVolume: getLegendValue(
            dataPoint.actualTradedVolume,
            numberFormat
        ),
        cumulativeActualVolume: getLegendValue(
            dataPoint.cumulativeTradedVolume,
            numberFormat
        ),
        expectedVolume: getLegendValue(
            dataPoint.historicalTradedVolume,
            numberFormat
        ),
        cumulativeExpectedVolume: getLegendValue(
            dataPoint.historicalCumulativeTradedVolume,
            numberFormat
        ),
        spread: getLegendValue(dataPoint.historicalSpread, numberFormat),
        volatility: getLegendValue(
            dataPoint.historicalVolatility,
            numberFormat
        ),
    });
}

function getLegendValue(value: number | undefined, numberFormat: NumberFormat) {
    return value !== undefined ? numberFormat.n0(value) : undefined;
}
